import { lazy, Suspense } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { FullScreenSpinner } from '@/components/ui/FullScreenSpinner'
import { useProject } from '@/data/useProject'
import { useProjectFiles } from '@/data/useProjectFiles'
import { formatDate } from '@/lib/format'
import { RouteCheckpoints } from './map/RouteCheckpoints'
import { StageBoard } from './stages/StageBoard'

// Leaflet is heavy — keep it out of the initial bundle.
const ProjectMap = lazy(() =>
  import('./map/ProjectMap').then((module) => ({ default: module.ProjectMap })),
)

export function ProjectDetailPage() {
  const { id = '' } = useParams()
  const { user, role } = useAuth()
  const { project, loading, error } = useProject(id)
  const { files } = useProjectFiles(id)

  if (loading) {
    return <FullScreenSpinner />
  }
  if (error) {
    return (
      <p role="alert" className="text-danger">
        {error}
      </p>
    )
  }
  if (!project) {
    return <p className="text-slate-500">Project not found.</p>
  }

  const backTo = role === 'admin' ? '/admin/projects' : role === 'client' ? '/client' : '/supplier'

  return (
    <div>
      <Link to={backTo} className="text-sm text-primary hover:underline">
        ← Back to projects
      </Link>

      <div className="mt-2 mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{project.name}</h1>
          <p className="text-slate-500">
            {project.productName} · {project.productQuantity} · {project.incoterms} ·{' '}
            {project.deliveryLocation}
          </p>
          <p className="text-sm text-slate-400">
            Supplier: {project.supplierName} · Client: {project.clientName}
          </p>
        </div>
        <div className="text-right">
          {project.status === 'completed' ? (
            <div>
              <span
                data-testid="project-status"
                className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700"
              >
                Completed
              </span>
              <p className="mt-1 text-xs text-slate-500">
                Completed {formatDate(project.completionDate)} · {project.durationDays} day
                {project.durationDays === 1 ? '' : 's'}
              </p>
            </div>
          ) : (
            <span
              data-testid="project-status"
              className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-700"
            >
              Ongoing
            </span>
          )}
          <p className="mt-1 text-xs text-slate-400">Started {formatDate(project.startDate)}</p>
        </div>
      </div>

      <div className="mb-6">
        <Suspense
          fallback={
            <div className="h-80 animate-pulse rounded-xl border border-slate-200 bg-slate-100 sm:h-[26rem]" />
          }
        >
          <ProjectMap
            geo={project.geo}
            deliveryLocation={project.deliveryLocation}
            waypoints={project.waypoints}
            deliveryStatus={project.deliveryStatus}
          />
        </Suspense>
      </div>

      {role && (
        <div className="mb-6">
          <RouteCheckpoints project={project} role={role} />
        </div>
      )}

      {role && user && (
        <StageBoard
          project={project}
          files={files}
          role={role}
          user={{ uid: user.uid, name: user.displayName ?? user.email ?? 'User' }}
        />
      )}
    </div>
  )
}
